import { useState, useRef, useEffect } from 'react';
import { classNames } from 'primereact/utils';
import { BundleCategoryItem } from '@/api';
import { ConfiguredCalendar, CloseIcon } from '@repo/atomic-components';
import { GreenButton } from '@/ui/components/GreenButton';
import { NewSectionAutocomplete } from './NewSectionAutocomplete';
import { format } from 'date-fns';
interface ExtractToPopupProps {
  categoriesVirtualList: BundleCategoryItem[];
  showNewCategoryAutocomplete: boolean;
  onClose: () => void;
  onExtract: (category: BundleCategoryItem | string) => void;
  onNewCategoryClick: () => void;
  onNewCategoryCreate: (value: string | BundleCategoryItem) => void;
  onNewCategoryCancel: () => void;
  setExtractedDate: (date: string | null) => void;
}

export const ExtractToPopup = ({
  categoriesVirtualList,
  showNewCategoryAutocomplete,
  onClose,
  onExtract,
  onNewCategoryClick,
  onNewCategoryCreate,
  onNewCategoryCancel,
  setExtractedDate,
}: ExtractToPopupProps) => {
  const [selectedCategory, setSelectedCategory] = useState<BundleCategoryItem | string | null>(null);
  const [currentDate, setCurrentDate] = useState<Date | null>(null);

  const [newlyCreatedCategory, setNewlyCreatedCategory] = useState<string | BundleCategoryItem | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleExtractClick = () => {
    if (selectedCategory) {
      onExtract(selectedCategory);
    }
  };

  const handleNewCategorySubmit = (value: string | BundleCategoryItem) => {
    // Add the new category to our local state and select it
    setSelectedCategory(value);
    setNewlyCreatedCategory(value);
    // Call the create handler to update the parent state
    onNewCategoryCreate(value);
  };

  // Auto-scroll to bottom when new category is created
  useEffect(() => {
    if (newlyCreatedCategory && scrollContainerRef.current) {
      // Scroll to bottom to show the newly created category
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  }, [newlyCreatedCategory]);

  // Combine existing categories with newly created one and selected suggestion for display
  const displayCategories = [...categoriesVirtualList];

  // Add newly created string category
  if (newlyCreatedCategory && typeof newlyCreatedCategory === 'string') {
    displayCategories.push({
      categoryId: 'new-category',
      categoryName: newlyCreatedCategory,
      inBundles: false,
    });
  }

  // Add selected category from suggestions list if it's not already in the main list
  if (
    selectedCategory &&
    typeof selectedCategory === 'object' &&
    !categoriesVirtualList.find((cat) => cat.categoryId === selectedCategory.categoryId)
  ) {
    displayCategories.push(selectedCategory);
  }

  return (
    <>
      <div className="fixed inset-0 z-[99]" onClick={onClose} />
      <div
        className={classNames(
          'z-100',
          'absolute right-0 bottom-0',
          'w-80',
          'border-fletchers-gray-110 rounded-lg border bg-white shadow-lg',
          'text-fletchers-black text-[14px]/[18px]',
          'transition-all duration-300',
        )}
      >
        {/* Header */}
        <div className="border-fletchers-gray-110 flex items-center justify-between border-b px-[24px] py-[16px]">
          <div className="text-fletchers-black-900 text-[18px]/[18px] font-[600]">Extract to a section</div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="cursor-pointer rounded p-1 focus:outline-none"
          >
            <CloseIcon width={12} height={12} />
          </button>
        </div>

        {/* Body */}
        <div className="px-[24px] py-[16px]">
          {/* Date Section */}
          <div className="mb-4">
            <label className="mb-2 block text-[14px] font-semibold">
              Override date <span className="text-fletchers-gray-175 font-light">(optional)</span>
            </label>
            <ConfiguredCalendar
              value={currentDate}
              onChange={(e) => {
                const date = e.value as Date;
                setCurrentDate(date);
                if (date) {
                  const formattedDate = format(date, 'yyyy-MM-dd');
                  setExtractedDate(formattedDate);
                }
              }}
              showIcon
              className="w-full [&_button]:!text-black [&_input]:!text-[14px] [&_input::placeholder]:!text-[14px]"
              appendTo="self"
              pt={{
                input: {
                  className: 'w-full p-2 border border-gray-300 rounded-md !text-[14px]',
                },
                panel: {
                  className: '!w-full',
                },
              }}
            />
          </div>

          {/* Section */}
          <div>
            <label className="mb-2 block text-[14px] font-semibold">Select or create a section</label>
            <div className="flex h-48 flex-col rounded-md border border-gray-300 bg-white">
              {/* Scrollable categories list */}
              <div className="flex-1 overflow-x-hidden overflow-y-auto" ref={scrollContainerRef}>
                {displayCategories.map((categoryItem) => (
                  <div
                    key={categoryItem.categoryId}
                    className={classNames(
                      'flex min-h-[32px] cursor-pointer items-center px-3 py-1',
                      'text-[14px] font-medium text-black',
                      'break-words whitespace-normal',
                      'hover:bg-fletchers-green-50',
                      {
                        'border-fletchers-green-300 bg-fletchers-green-50 border-l-2':
                          (typeof selectedCategory === 'object' &&
                            selectedCategory?.categoryId === categoryItem.categoryId) ||
                          (typeof selectedCategory === 'string' && selectedCategory === categoryItem.categoryName),
                        'border-l-2 border-l-transparent': !(
                          (typeof selectedCategory === 'object' &&
                            selectedCategory?.categoryId === categoryItem.categoryId) ||
                          (typeof selectedCategory === 'string' && selectedCategory === categoryItem.categoryName)
                        ),
                      },
                    )}
                    onClick={() =>
                      setSelectedCategory(
                        categoryItem.categoryId === 'new-category' ? categoryItem.categoryName : categoryItem,
                      )
                    }
                  >
                    {categoryItem.categoryName}
                  </div>
                ))}
              </div>

              {/* Fixed New section option at bottom */}
              <div
                className="relative cursor-pointer border-t border-gray-200 p-3 text-[14px] font-medium text-black hover:bg-green-50"
                onClick={onNewCategoryClick}
              >
                {showNewCategoryAutocomplete ? (
                  <div className="relative">
                    <NewSectionAutocomplete onSubmit={handleNewCategorySubmit} onCancel={onNewCategoryCancel} />
                  </div>
                ) : (
                  '+ New section'
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end px-[24px] pb-[24px]">
          <GreenButton
            label="Extract"
            icon="pi pi-plus !text-[12px]"
            disabled={!selectedCategory}
            onClick={handleExtractClick}
            className="!h-[32px]"
          />
        </div>
      </div>
    </>
  );
};
